import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action } from '@ngrx/store';
import { CartActions, CartEntryConnector } from '@spartacus/cart/base/core'
import { CartModification } from '@spartacus/cart/base/root'
import {
  GlobalMessageActions,
  GlobalMessageType,
  LoggerService,
  SiteContextActions,
  normalizeHttpError,
  withdrawOn
} from '@spartacus/core'
import { Observable, from } from 'rxjs'
import { catchError, concatMap, map, switchMap } from 'rxjs/operators'
import { EBCartModificationList } from '../../interface/eb-cart.interface'
import { EbCartAdapter } from '../../services/connectors/eb-cart.adapter'
import * as EbCartEntryAction from '../actions/eb-cart-entry.action'

@Injectable()
export class EbCartEntryEffects {
  private readonly contextChange$ = this.actions$.pipe(
    ofType(SiteContextActions.CURRENCY_CHANGE, SiteContextActions.LANGUAGE_CHANGE)
  )
  protected logger = inject(LoggerService);

  addEntry$: Observable<
    | CartActions.CartAddEntrySuccess
    | CartActions.CartAddEntryFail
    | CartActions.LoadCart
    | GlobalMessageActions.AddMessage
    | Action<any>
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(EbCartEntryAction.EB_CART_ADD_ENTRY),
      switchMap((action: EbCartEntryAction.EbCartAddEntry) => {
        const { payload } = action
        return this.cartEntryConnector
          .add(payload.userId, payload.cartId, payload.productCode, payload.quantity)
          .pipe(
            switchMap((cartModification: CartModification) => {
              const actions: Action<any>[] = [
                new CartActions.CartAddEntrySuccess({
                  ...payload,
                  ...(cartModification as Required<CartModification>)
                })
              ]
              if (cartModification.statusCode === 'maxOrderQuantityExceeded') {
                actions.push(
                  new GlobalMessageActions.AddMessage({
                    text: {
                      key: 'addToCart.maxOrderQuantityExceeded'
                    },
                    type: GlobalMessageType.MSG_TYPE_WARNING,
                    timeout: Number.MAX_VALUE
                  })
                )
              }
              return actions
            }),
            catchError(error =>
              from([
                new CartActions.CartAddEntryFail({
                  ...payload,
                  error: normalizeHttpError(error, this.logger)
                }),
                new CartActions.LoadCart({
                  cartId: payload.cartId,
                  userId: payload.userId
                })
              ])
            )
          )
      }),
      withdrawOn(this.contextChange$)
    )
  )

  addEntries$: Observable<
    | EbCartEntryAction.EbCartAddEntriesSuccess
    | CartActions.LoadCart
    | GlobalMessageActions.AddMessage
    | Action<any>
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(EbCartEntryAction.EB_CART_ADD_ENTRIES),
      concatMap((action: EbCartEntryAction.EbCartAddEntries) => {
        const { payload } = action
        return this.cartAdapter.addEntries(payload.cartId, payload.userId, payload.entries).pipe(
          concatMap((cartModification: EBCartModificationList) => {
            const actions: Action<any>[] = [
              new EbCartEntryAction.EbCartAddEntriesSuccess({
                cartId: payload.cartId,
                cartModification
              }),
              EbCartEntryAction.setCartModifications({
                result: cartModification
              }),
              new CartActions.LoadCart({
                cartId: payload.cartId,
                userId: payload.userId
              })
            ]
            cartModification?.cartModifications.forEach(value => {
              if (value.statusCode === 'maxOrderQuantityExceeded') {
                actions.push(
                  new GlobalMessageActions.AddMessage({
                    text: {
                      key: 'addToCart.maxOrderQuantityExceeded'
                    },
                    type: GlobalMessageType.MSG_TYPE_WARNING,
                    timeout: Number.MAX_VALUE
                  })
                )
              }
            })
            return actions
          }),
          catchError(error =>
            from([
              new EbCartEntryAction.EbCartAddEntriesFail({
                cartId: payload.cartId,
                error: normalizeHttpError(error, this.logger)
              }),
              new CartActions.LoadCart({
                cartId: payload.cartId,
                userId: payload.userId
              })
            ])
          )
        )
      }),
      withdrawOn(this.contextChange$)
    )
  )

  removeEntry$: Observable<
    CartActions.CartRemoveEntrySuccess | CartActions.CartRemoveEntryFail | CartActions.LoadCart
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(EbCartEntryAction.EB_CART_REMOVE_ENTRY),
      switchMap((action: EbCartEntryAction.EbCartRemoveEntry) => {
        const { payload } = action
        return this.cartEntryConnector
          .remove(payload.userId, payload.cartId, payload.entryNumber)
          .pipe(
            map(() => {
              return new CartActions.CartRemoveEntrySuccess({
                ...payload
              })
            }),
            catchError(error =>
              from([
                new CartActions.CartRemoveEntryFail({
                  ...payload,
                  error: normalizeHttpError(error, this.logger)
                }),
                new CartActions.LoadCart({
                  cartId: payload.cartId,
                  userId: payload.userId
                })
              ])
            )
          )
      }),
      withdrawOn(this.contextChange$)
    )
  )

  updateEntry$: Observable<
    CartActions.CartUpdateEntrySuccess | CartActions.CartUpdateEntryFail | CartActions.LoadCart
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(EbCartEntryAction.EB_CART_UPDATE_ENTRY),
      switchMap((action: EbCartEntryAction.EbCartUpdateEntry) => {
        const { payload } = action
        return this.cartEntryConnector
          .update(payload.userId, payload.cartId, payload.entryNumber, payload.quantity)
          .pipe(
            map(() => {
              return new CartActions.CartUpdateEntrySuccess({
                ...payload
              })
            }),
            catchError(error =>
              from([
                new CartActions.CartUpdateEntryFail({
                  ...payload,
                  error: normalizeHttpError(error, this.logger)
                }),
                new CartActions.LoadCart({
                  cartId: payload.cartId,
                  userId: payload.userId
                })
              ])
            )
          )
      }),
      withdrawOn(this.contextChange$)
    )
  )

  constructor(
    private readonly actions$: Actions,
    private readonly cartEntryConnector: CartEntryConnector,
    private readonly cartAdapter: EbCartAdapter
  ) {}
}
